/**
 * Auth API Mutations
 * React Query mutation hooks for authentication, tenants, and user management
 *
 * Gateway mapping: /auth/* → ai-auth service
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiPatch, apiPost, apiPut } from '@/api/_shared/http';
import { handleAccessTokenStorage, handlePopupFlow, clearAccessTokenStorage, isCasSession, markCasSession } from '@/utils/authSession';
import { getSafeRedirectPath } from '@/utils/redirect';
import { authKeys } from './keys';
import type { TenantRoleType } from '@/constants/tenantRoles';
import type { AuthResponse, RegisterRequest, RegisterResponse, ProfileResponse, UpdateProfileRequest, InitiateProfilePhoneRequest, InitiateProfilePhoneResponse, InitiateProfileEmailRequest, InitiateProfileEmailResponse, VerifyProfilePhoneRequest, ChangePasswordRequest, ChangePasswordResponse, SetPasswordRequest, SetPasswordResponse, TenantSwitchRequest, TenantSwitchResponse, CreateClientRequest, CreateClientResponse, CreateCompanyRequest, CreateCompanyResponse, IssueTenantRoleTokenRequest, IssueTenantRoleTokenResponse, UpdateCompanyRequest, UpdateCompanyResponse, UpdateCompanyMemberRoleRequest, UpdateCompanyMemberRoleResponse, UpdateUserRoleRequest, UpdateUserRoleResponse, ForgotPasswordRequest, ForgotPasswordResponse, ResetPasswordRequest, ResetPasswordResponse, VerifyEmailRequest, VerifyEmailResponse, ResendVerificationRequest, ResendVerificationResponse, AdminResendVerificationResponse, AdminVerifyEmailResponse, AdminCreateUserRequest, AdminCreateUserResponse, UpdateWeeklySummarySettingsRequest, WeeklySummarySettingsResponse, AssignUserToTenantInput, LoginRequest, InitiateSmsRegistrationRequest, VerifySmsCodeRequest, InitiateSmsLoginRequest } from './interfaces';
import { getGatewayUrl } from '@/services/http-client';

import { AUTH_BASE } from './paths';
/**
 * Refreshes every view that shows tenant memberships after one changes: the tenant page
 * (employees, customers, client), the user detail page and the admin users list.
 * @param queryClient - React Query client.
 * @param scope - Tenant whose members changed and, when known, the affected user.
 */
const invalidateMembershipQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  { clientId, userId }: { clientId?: string; userId?: string }
) => {
  if (clientId) {
    queryClient.invalidateQueries({ queryKey: authKeys.clientEmployees(clientId) });
    queryClient.invalidateQueries({ queryKey: authKeys.clientCustomers(clientId) });
    queryClient.invalidateQueries({ queryKey: authKeys.client(clientId) });
  }
  if (userId) {
    queryClient.invalidateQueries({ queryKey: authKeys.userTenantsByUserId(userId) });
  }
  queryClient.invalidateQueries({ queryKey: authKeys.usersWithTenantsAll() });
};

// ============================================================================
// MUTATION FUNCTIONS (Reusable API calls)
// ============================================================================

/**
 * Registers a new user account
 */
export const registerMutationFn = async (body: RegisterRequest): Promise<RegisterResponse> =>
  apiPost<RegisterResponse>(`${AUTH_BASE}/register`, body);

/**
 * Logs in a user with email and password
 */
export const loginMutationFn = async (body: LoginRequest): Promise<AuthResponse> => apiPost<AuthResponse>(`${AUTH_BASE}/login`, body);

export const forgotPasswordMutationFn = async (body: ForgotPasswordRequest): Promise<ForgotPasswordResponse> =>
  apiPost<ForgotPasswordResponse>(`${AUTH_BASE}/forgot-password`, body);

export const resetPasswordMutationFn = async (body: ResetPasswordRequest): Promise<ResetPasswordResponse> =>
  apiPost<ResetPasswordResponse>(`${AUTH_BASE}/reset-password`, body);

export const verifyEmailMutationFn = async (body: VerifyEmailRequest): Promise<VerifyEmailResponse> =>
  apiPost<VerifyEmailResponse>(`${AUTH_BASE}/verify-email`, body);

export const resendVerificationMutationFn = async (
  body: ResendVerificationRequest
): Promise<ResendVerificationResponse> =>
  apiPost<ResendVerificationResponse>(`${AUTH_BASE}/resend-verification`, body);

export const adminResendVerificationMutationFn = async (
  userId: string
): Promise<AdminResendVerificationResponse> =>
  apiPost<AdminResendVerificationResponse>(`${AUTH_BASE}/users/${userId}/resend-verification`);

export const adminVerifyEmailMutationFn = async (userId: string): Promise<AdminVerifyEmailResponse> =>
  apiPost<AdminVerifyEmailResponse>(`${AUTH_BASE}/users/${userId}/verify-email`);

export const adminCreateUserMutationFn = async (
  body: AdminCreateUserRequest
): Promise<AdminCreateUserResponse> => apiPost<AdminCreateUserResponse>(`${AUTH_BASE}/users`, body);

/**
 * Logs out the current user
 */
export const logoutMutationFn = async (): Promise<void> => {
  console.log('Logging out');
  await apiPost<void>(`${AUTH_BASE}/logout`);
};

/**
 * Initiates SMS registration process
 */
export const initiateSmsRegistrationMutationFn = async (body: InitiateSmsRegistrationRequest) => apiPost<unknown>(`${AUTH_BASE}/sms/register/initiate`, body);

/**
 * Verifies SMS registration with code
 */
export const verifySmsRegistrationMutationFn = async (body: VerifySmsCodeRequest): Promise<AuthResponse> => apiPost<AuthResponse>(`${AUTH_BASE}/sms/register/verify`, body);

/**
 * Initiates SMS login process
 */
export const initiateSmsLoginMutationFn = async (body: InitiateSmsLoginRequest) => apiPost<unknown>(`${AUTH_BASE}/sms/login/initiate`, body);

/**
 * Verifies SMS login with code
 */
export const verifySmsLoginMutationFn = async (body: VerifySmsCodeRequest): Promise<AuthResponse> => apiPost<AuthResponse>(`${AUTH_BASE}/sms/login/verify`, body);

/**
 * Logs in user with Google OAuth
 */
export const googleLoginMutationFn = async (): Promise<AuthResponse> => {
  const result = await handlePopupFlow(
    `${AUTH_BASE}/google?return_json=true`,
    'googleLogin',
    'GOOGLE_LOGIN_SUCCESS',
    'GOOGLE_LOGIN_ERROR'
  );
  return result.data as AuthResponse;
};

/** App name ai-auth uses to send the browser back to the GUI after CAS. */
const CAS_APP = 'gui';

/**
 * Builds an absolute ai-auth CAS URL on the public api-gateway (the same host for every frontend),
 * for top-level browser navigation.
 * @param path - CAS endpoint under the auth base, e.g. `cas/login`.
 * @param params - Extra query parameters.
 */
const buildCasGatewayUrl = (path: string, params: Record<string, string> = {}): string => {
  const gatewayBase = getGatewayUrl();
  const query = new URLSearchParams({ app: CAS_APP, ...params });
  return `${gatewayBase}${AUTH_BASE}/${path}?${query}`;
};

/**
 * Starts CAS single sign-on with a full-page redirect. ai-auth sends the browser to CAS and,
 * after login, back to /sso/cas/complete with a one-time code.
 * @param redirect - Optional same-origin path to open after login.
 */
export const startCasLogin = (redirect?: string): void => {
  const returnPath = getSafeRedirectPath(redirect) ?? '/';
  window.location.assign(buildCasGatewayUrl('cas/login', { returnPath }));
};

/**
 * Exchanges the one-time CAS handoff code for an access token (refresh cookie is set by the gateway).
 */
export const casExchangeMutationFn = async (code: string): Promise<AuthResponse> =>
  apiPost<AuthResponse>(`${AUTH_BASE}/cas/exchange`, { code });

/**
 * Updates user profile information
 */
export const updateProfileMutationFn = async (body: UpdateProfileRequest): Promise<ProfileResponse> =>
  apiPut<ProfileResponse>(`${AUTH_BASE}/profile`, body);

export const initiateProfilePhoneMutationFn = async (
  body: InitiateProfilePhoneRequest
): Promise<InitiateProfilePhoneResponse> =>
  apiPost<InitiateProfilePhoneResponse>(`${AUTH_BASE}/profile/phone/initiate`, body);

export const verifyProfilePhoneMutationFn = async (
  body: VerifyProfilePhoneRequest
): Promise<ProfileResponse> => apiPost<ProfileResponse>(`${AUTH_BASE}/profile/phone/verify`, body);

export const initiateProfileEmailMutationFn = async (
  body: InitiateProfileEmailRequest
): Promise<InitiateProfileEmailResponse> =>
  apiPost<InitiateProfileEmailResponse>(`${AUTH_BASE}/profile/email/initiate`, body);

/**
 * Changes the authenticated user's password (email/password accounts only).
 */
export const changePasswordMutationFn = async (body: ChangePasswordRequest): Promise<ChangePasswordResponse> =>
  apiPost<ChangePasswordResponse>(`${AUTH_BASE}/change-password`, body);

export const setPasswordMutationFn = async (body: SetPasswordRequest): Promise<SetPasswordResponse> =>
  apiPost<SetPasswordResponse>(`${AUTH_BASE}/set-password`, body);

/**
 * Switches the current tenant context
 */
export const switchTenantMutationFn = async (body: TenantSwitchRequest): Promise<TenantSwitchResponse> =>
  apiPost<TenantSwitchResponse>(`${AUTH_BASE}/tenants/switch`, body);

/**
 * Creates a new client (System Admin only)
 */
export const createClientMutationFn = async (body: CreateClientRequest): Promise<CreateClientResponse> =>
  apiPost<CreateClientResponse>(`${AUTH_BASE}/tenants/clients`, body);

/**
 * Creates a new company (Client Owner only)
 */
export const createCompanyMutationFn = async (body: CreateCompanyRequest): Promise<CreateCompanyResponse> =>
  apiPost<CreateCompanyResponse>(`${AUTH_BASE}/tenants/companies`, body);

/**
 * Updates an existing company (System Admin or Tenant Owner)
 */
export const updateCompanyMutationFn = async (
  companyId: string,
  body: UpdateCompanyRequest
): Promise<UpdateCompanyResponse> =>
  apiPatch<UpdateCompanyResponse>(`${AUTH_BASE}/tenants/companies/${companyId}`, body);

/**
 * Deletes a company (Admin or client/company owner). Cascades to members and tokens.
 */
export const deleteCompanyMutationFn = async (companyId: string): Promise<void> => {
  await apiDelete<void>(`${AUTH_BASE}/tenants/companies/${companyId}`);
};

/**
 * Issues a tenant role token (global ADMIN or DEVELOPER; used from admin tenant UI)
 */
export const issueTenantRoleTokenMutationFn = async (
  body: IssueTenantRoleTokenRequest
): Promise<IssueTenantRoleTokenResponse> =>
  apiPost<IssueTenantRoleTokenResponse>(`${AUTH_BASE}/admin/tenant-role-tokens`, body);

/**
 * Issues a tenant role token for a client (Owner or System Admin)
 * Owner can only issue for their own client.
 */
export const issueTenantRoleTokenForClientMutationFn = async (
  clientId: string,
  body: { tenantRole: TenantRoleType; companyId?: string }
): Promise<IssueTenantRoleTokenResponse> =>
  apiPost<IssueTenantRoleTokenResponse>(`${AUTH_BASE}/client/${clientId}/tenant-role-tokens`, body);

/**
 * Updates a user's global role (System Admin only)
 */
export const updateUserRoleMutationFn = async (
  userId: string,
  body: UpdateUserRoleRequest
): Promise<UpdateUserRoleResponse> => apiPut<UpdateUserRoleResponse>(`${AUTH_BASE}/users/${userId}/role`, body);

/**
 * Updates a client member's tenant role (Owner or System Admin)
 */
export const updateTenantRoleMutationFn = async (
  clientId: string,
  userId: string,
  body: { tenantRole: TenantRoleType }
): Promise<{ id: string; role: string; isOwner: boolean }> =>
  apiPatch<{ id: string; role: string; isOwner: boolean }>(
    `${AUTH_BASE}/client/${clientId}/members/${userId}/role`,
    body
  );

/**
 * Updates a company member's tenant role (Admin or client owner)
 */
export const updateCompanyMemberRoleMutationFn = async (
  companyId: string,
  userId: string,
  body: UpdateCompanyMemberRoleRequest
): Promise<UpdateCompanyMemberRoleResponse> =>
  apiPatch<UpdateCompanyMemberRoleResponse>(
    `${AUTH_BASE}/company/${companyId}/members/${userId}/role`,
    body
  );

/**
 * Removes a user from a company (Admin or client owner)
 */
export const removeCompanyMemberMutationFn = async (companyId: string, userId: string): Promise<void> => {
  await apiDelete<void>(`${AUTH_BASE}/company/${companyId}/members/${userId}`);
};

/**
 * Removes a user from a tenant/client (Admin or client owner)
 */
export const removeClientMemberMutationFn = async (clientId: string, userId: string): Promise<void> => {
  await apiDelete<void>(`${AUTH_BASE}/client/${clientId}/members/${userId}`);
};

export const updateWeeklySummarySettingsMutationFn = async (
  clientId: string,
  body: UpdateWeeklySummarySettingsRequest
): Promise<WeeklySummarySettingsResponse> =>
  apiPut<WeeklySummarySettingsResponse>(`${AUTH_BASE}/client/${clientId}/weekly-summary-settings`, body);

// ============================================================================
// REACT MUTATION HOOKS
// ============================================================================

/**
 * Hook to register a new user account
 */
export const useRegisterMutation = () => {
  return useMutation({
    mutationFn: registerMutationFn
  });
};

/**
 * Hook to log in a user with email and password
 */
export const useForgotPasswordMutation = () => {
  return useMutation({
    mutationFn: forgotPasswordMutationFn
  });
};

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: resetPasswordMutationFn
  });
};

export const useVerifyEmailMutation = () => {
  return useMutation({
    mutationFn: verifyEmailMutationFn
  });
};

export const useResendVerificationMutation = () => {
  return useMutation({
    mutationFn: resendVerificationMutationFn
  });
};

export const useLoginMutation = () => {
  return useMutation({
    mutationFn: loginMutationFn,
    onSuccess: (data) => {
      console.log('[LoginFlow] useLoginMutation onSuccess: BEFORE handleAccessTokenStorage, role=', data?.user?.role);
      handleAccessTokenStorage(data);
      console.log('[LoginFlow] useLoginMutation onSuccess: AFTER handleAccessTokenStorage (auth:changed dispatched)');
      // Auth state will be updated via the auth:changed event
    },
    onError: (error) => {
      console.error('Login failed:', error);
    }
  });
};

/**
 * Hook to log out the current user
 */
export const useLogoutMutation = () => {
  return useMutation({
    mutationFn: logoutMutationFn,
    onSettled: () => {
      // Read before clearing: CAS sessions must also end the CAS SSO session
      const endCasSession = isCasSession();
      clearAccessTokenStorage();
      if (endCasSession) {
        window.location.assign(buildCasGatewayUrl('cas/logout'));
        return;
      }
      // Trigger a page reload to update auth state across the app
      window.location.reload();
    }
  });
};

/**
 * Hook to initiate SMS registration process
 */
export const useInitiateSmsRegistrationMutation = () => {
  return useMutation({
    mutationFn: initiateSmsRegistrationMutationFn
  });
};

/**
 * Hook to verify SMS registration with code
 */
export const useVerifySmsRegistrationMutation = () => {
  return useMutation({
    mutationFn: verifySmsRegistrationMutationFn,
    onSuccess: (data) => {
      handleAccessTokenStorage(data);
      // Auth state will be updated via the auth:changed event
    },
    onError: (error) => {
      console.error('SMS registration verification failed:', error);
    }
  });
};

/**
 * Hook to initiate SMS login process
 */
export const useInitiateSmsLoginMutation = () => {
  return useMutation({
    mutationFn: initiateSmsLoginMutationFn
  });
};

/**
 * Hook to verify SMS login with code
 */
export const useVerifySmsLoginMutation = () => {
  return useMutation({
    mutationFn: verifySmsLoginMutationFn,
    onSuccess: (data) => {
      handleAccessTokenStorage(data);
      // Auth state will be updated via the auth:changed event
    },
    onError: (error) => {
      console.error('SMS login verification failed:', error);
    }
  });
};

/**
 * Hook to log in user with Google OAuth
 */
/**
 * Hook to complete CAS login by exchanging the handoff code for tokens
 */
export const useCasExchangeMutation = () => {
  return useMutation({
    mutationFn: casExchangeMutationFn,
    onSuccess: (data) => {
      handleAccessTokenStorage(data);
      markCasSession();
    },
    onError: (error) => {
      console.error('CAS login failed:', error);
    }
  });
};

/**
 * Google login through the OAuth popup; auth state updates via the `auth:changed` event
 * that the popup flow dispatches after storing the token.
 */
export const useGoogleLoginMutation = () => {
  return useMutation({
    mutationFn: googleLoginMutationFn,
    onError: (error) => {
      console.error('Google login failed:', error);
    }
  });
};

/**
 * Hook to update user profile information
 */
export const useUpdateProfileMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: updateProfileMutationFn,
    onSuccess: (data) => {
      // Invalidate and refetch profile data
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
      queryClient.setQueryData(authKeys.profile(), data);
    },
    onError: (error) => {
      console.error('Profile update failed:', error);
    }
  });
};

export const useInitiateProfilePhoneMutation = () => {
  return useMutation({
    mutationFn: initiateProfilePhoneMutationFn
  });
};

export const useVerifyProfilePhoneMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyProfilePhoneMutationFn,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
      queryClient.setQueryData(authKeys.profile(), data);
    },
    onError: (error) => {
      console.error('Profile phone verification failed:', error);
    }
  });
};

export const useInitiateProfileEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: initiateProfileEmailMutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
    }
  });
};

/**
 * Hook to change the authenticated user's password.
 */
export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: changePasswordMutationFn,
    onError: (error) => {
      console.error('Change password failed:', error);
    }
  });
};

export const useSetPasswordMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setPasswordMutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
    },
    onError: (error) => {
      console.error('Set password failed:', error);
    }
  });
};

/**
 * Hook to switch the current tenant context
 */
export const useSwitchTenantMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: switchTenantMutationFn,
    onSuccess: (data) => {
      handleAccessTokenStorage({ accessToken: data.accessToken, user: {} as any });
      // Invalidate all queries so views refetch with the new tenant context
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error('Tenant switch failed:', error);
    }
  });
};

/**
 * Hook to create a new client (System Admin only)
 */
export const useCreateClientMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createClientMutationFn,
    onSuccess: () => {
      // Invalidate and refetch clients-with-companies query
      queryClient.invalidateQueries({ queryKey: authKeys.clientsWithCompaniesAll() });
    },
    onError: (error) => {
      console.error('Failed to create client:', error);
    }
  });
};

/**
 * Hook to create a new company (Client Owner only)
 */
export const useCreateCompanyMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createCompanyMutationFn,
    onSuccess: () => {
      // Invalidate and refetch clients-with-companies query to refresh UI
      queryClient.invalidateQueries({ queryKey: authKeys.clientsWithCompaniesAll() });
    },
    onError: (error) => {
      console.error('Failed to create company:', error);
    }
  });
};

/**
 * Hook to update an existing company (System Admin or Tenant Owner)
 */
export const useUpdateCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, data }: { companyId: string; data: UpdateCompanyRequest }) =>
      updateCompanyMutationFn(companyId, data),
    onSuccess: (data, variables) => {
      // Invalidate and refetch company- and client-related queries to refresh UI
      queryClient.invalidateQueries({ queryKey: authKeys.clientsWithCompaniesAll() });
      queryClient.invalidateQueries({ queryKey: authKeys.company(variables.companyId) });
      if (data.company.clientId) {
        queryClient.invalidateQueries({ queryKey: authKeys.client(data.company.clientId) });
      }
    },
    onError: (error) => {
      console.error('Failed to update company:', error);
    }
  });
};

/**
 * Hook to delete a company (Admin or client/company owner). Pass clientId to invalidate client cache.
 */
export const useDeleteCompanyMutation = (clientId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) => deleteCompanyMutationFn(companyId),
    onSuccess: (_data, companyId, context) => {
      queryClient.invalidateQueries({ queryKey: authKeys.clientsWithCompaniesAll() });
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: authKeys.client(clientId) });
      }
      queryClient.invalidateQueries({ queryKey: authKeys.company(companyId) });
    },
    onError: (error) => {
      console.error('Failed to delete company:', error);
    }
  });
};

/**
 * Hook to issue a tenant role token (System Admin only)
 */
export const useIssueTenantRoleTokenMutation = () => {
  return useMutation({
    mutationFn: issueTenantRoleTokenMutationFn,
    onError: (error) => {
      console.error('Failed to issue tenant role token:', error);
    }
  });
};

/**
 * Hook to issue a tenant role token for a client (Owner or System Admin)
 */
export const useIssueTenantRoleTokenForClientMutation = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { tenantRole: TenantRoleType; companyId?: string }) =>
      issueTenantRoleTokenForClientMutationFn(clientId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.clientEmployees(clientId) });
    },
    onError: (error) => {
      console.error('Failed to issue tenant role token for client:', error);
    }
  });
};

/**
 * Hook to update a client member's tenant role (Owner or System Admin)
 */
export const useUpdateTenantRoleMutation = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, tenantRole }: { userId: string; tenantRole: TenantRoleType }) =>
      updateTenantRoleMutationFn(clientId, userId, { tenantRole }),
    onSuccess: (_data, { userId }) => {
      invalidateMembershipQueries(queryClient, { clientId, userId });
    },
    onError: (error) => {
      console.error('Failed to update tenant role:', error);
    }
  });
};

/**
 * Hook to remove a user from a tenant/client (Admin or client owner)
 */
export const useRemoveClientMemberMutation = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeClientMemberMutationFn(clientId, userId),
    onSuccess: (_data, userId) => {
      invalidateMembershipQueries(queryClient, { clientId, userId });
    },
    onError: (error) => {
      console.error('Failed to remove client member:', error);
    }
  });
};

export const useUpdateWeeklySummarySettingsMutation = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateWeeklySummarySettingsRequest) =>
      updateWeeklySummarySettingsMutationFn(clientId, body),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.weeklySummarySettings(clientId), data);
    }
  });
};

/**
 * Hook to update any company member's role (for tables with multiple companies).
 * Pass clientId to invalidate client-employees on success.
 */
export const useUpdateAnyCompanyMemberRoleMutation = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      userId,
      tenantRole
    }: {
      companyId: string;
      userId: string;
      tenantRole: TenantRoleType;
    }) => updateCompanyMemberRoleMutationFn(companyId, userId, { tenantRole }),
    onSuccess: (_data, { userId }) => {
      invalidateMembershipQueries(queryClient, { clientId, userId });
    },
    onError: (error) => {
      console.error('Failed to update company member role:', error);
    }
  });
};

/**
 * Hook to remove any company member (for tables with multiple companies).
 */
export const useRemoveAnyCompanyMemberMutation = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, userId }: { companyId: string; userId: string }) =>
      removeCompanyMemberMutationFn(companyId, userId),
    onSuccess: (_data, { userId }) => {
      invalidateMembershipQueries(queryClient, { clientId, userId });
    },
    onError: (error) => {
      console.error('Failed to remove company member:', error);
    }
  });
};

/**
 * Hook to assign an existing user to a tenant and, optionally, to some of its companies in one step.
 * The tenant membership is created first; company memberships are then added one by one and the
 * IDs of any that failed are returned so the caller can report a partial success.
 */
export const useAssignUserToTenantMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, clientId, tenantRole, companyIds = [], companyRole }: AssignUserToTenantInput) => {
      await updateTenantRoleMutationFn(clientId, userId, { tenantRole });
      if (!companyRole || companyIds.length === 0) {
        return { failedCompanyIds: [] as string[] };
      }
      const results = await Promise.allSettled(
        companyIds.map((companyId) => updateCompanyMemberRoleMutationFn(companyId, userId, { tenantRole: companyRole }))
      );
      return {
        failedCompanyIds: companyIds.filter((_companyId, index) => results[index].status === 'rejected')
      };
    },
    onSettled: (_data, _error, { userId, clientId }) => {
      invalidateMembershipQueries(queryClient, { clientId, userId });
    }
  });
};

/**
 * Hook to update a user's global role (System Admin only)
 */
export const useUpdateUserRoleMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => 
      updateUserRoleMutationFn(userId, { role }),
    onSuccess: (data, variables) => {
      // Invalidate users-related queries to refresh UI
      queryClient.invalidateQueries({ queryKey: authKeys.usersList() });
      queryClient.invalidateQueries({ queryKey: authKeys.usersWithTenantsAll() });
      queryClient.invalidateQueries({ queryKey: authKeys.userTenantsByUserId(variables.userId) });
    },
    onError: (error) => {
      console.error('Failed to update user role:', error);
    }
  });
};

/**
 * Hook for admins/developers to resend email verification for a user.
 */
export const useAdminResendVerificationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => adminResendVerificationMutationFn(userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: authKeys.usersWithTenantsAll() });
      queryClient.invalidateQueries({ queryKey: authKeys.userTenantsByUserId(userId) });
    },
    onError: (error) => {
      console.error('Failed to resend verification email:', error);
    }
  });
};

/**
 * Hook for admins/developers to manually mark a user's email as verified.
 */
export const useAdminVerifyEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => adminVerifyEmailMutationFn(userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: authKeys.usersWithTenantsAll() });
      queryClient.invalidateQueries({ queryKey: authKeys.userTenantsByUserId(userId) });
    },
    onError: (error) => {
      console.error('Failed to verify user email:', error);
    }
  });
};

/**
 * Hook for admins/developers to create a pre-verified user.
 */
export const useAdminCreateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AdminCreateUserRequest) => adminCreateUserMutationFn(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.usersList() });
      queryClient.invalidateQueries({ queryKey: authKeys.usersWithTenantsAll() });
    },
    onError: (error) => {
      console.error('Failed to create user:', error);
    }
  });
};
